import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { addFavoriteNetwork, addFavoriteLine, removeFavoriteLine, removeFavoriteNetwork } from '../services/favorites';
import { useAuth } from '../context/AuthContext';
import FavoriteButton from './FavoriteButton';
import NetworkLogo from './NetworkLogo';
import { getCachedData } from '../utils/supabaseCache';
import TextMarquee from './TextMarquee';
import AlertBanner from './AlertBanner';

// Définir une couleur constante pour les favoris
const FAVORITE_COLOR = '#FFCA28'; // Jaune amber-400 cohérent

// Style pour l'animation des étoiles et le texte défilant
const additionalStyles = `
  @keyframes starPulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.2); }
    100% { transform: scale(1); }
  }

  .star-pulse {
    animation: starPulse 0.5s ease-out;
  }
  
  .star-shadow {
    filter: drop-shadow(0 0 4px rgba(251, 191, 36, 0.7));
  }
  
  .marquee-container {
    width: 100%;
    overflow: hidden;
    position: relative;
  }
  
  .marquee-content {
    display: inline-flex;
    white-space: nowrap;
    animation: marqueeAnimation 20s linear infinite;
  }
  
  .marquee-content:hover {
    animation-play-state: paused;
  }
  
  @keyframes marqueeAnimation {
    from { transform: translateX(0%); }
    to { transform: translateX(-50%); }
  }
  
  .separator {
    margin: 0 8px;
    opacity: 0.6;
  }
  
  .fade-left, .fade-right {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 15px;
    z-index: 2;
    pointer-events: none;
  }
  
  .fade-left {
    left: 0;
    background: linear-gradient(to right, white, rgba(255, 255, 255, 0));
  }

  .dark .fade-left {
    background: linear-gradient(to right, #1e293b, rgba(30, 41, 59, 0));
  }
  
  .fade-right {
    right: 0;
    background: linear-gradient(to left, white, rgba(255, 255, 255, 0));
  }

  .dark .fade-right {
    background: linear-gradient(to left, #1e293b, rgba(30, 41, 59, 0));
  }
  
  .page-title {
    font-size: 1.6rem;
    font-weight: bold;
    color: #1e3a8a;
    margin-bottom: 0.3rem;
  }
  
  .text-gradient {
    background-image: linear-gradient(135deg, #07d6fb, #ff66c4);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    font-weight: 700;
    font-size: 1.6rem;
    text-shadow: 0px 0px 1px rgba(0,0,0,0.05);
  }
  
  .gradient-hr {
    height: 2px;
    border: none;
    background-image: linear-gradient(135deg, #07d6fb, #ff66c4);
    border-radius: 1px;
    margin-top: 0;
    margin-bottom: 1rem;
    width: 120px;
  }
  
  .search-container {
    max-width: 100%;
  }
  
  .search-input {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 2.5rem;
    border-radius: 2rem;
    background-color: #f8fafc;
    transition: all 0.2s;
    border: double 1px transparent;
    background-image: linear-gradient(white, white), 
                      linear-gradient(135deg, #07d6fb, #ff66c4);
    background-origin: border-box;
    background-clip: padding-box, border-box;
  }
  
  .dark .search-input {
    background-image: linear-gradient(#1e293b, #1e293b), 
                      linear-gradient(135deg, #07d6fb, #ff66c4);
  }
  
  .search-input:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.2);
  }

  .dark .search-input:focus {
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.3);
  }
  
  .search-icon {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
  }
`;

// Fonction pour assombrir une couleur hexadécimale
const darkenColor = (color) => {
  if (!color) return '6B7280';
  
  const r = parseInt(color.slice(0, 2), 16);
  const g = parseInt(color.slice(2, 4), 16);
  const b = parseInt(color.slice(4, 6), 16);
  
  const darkenAmount = 0.85;
  const darkenR = Math.floor(r * darkenAmount);
  const darkenG = Math.floor(g * darkenAmount);
  const darkenB = Math.floor(b * darkenAmount);
  
  return `${darkenR.toString(16).padStart(2, '0')}${darkenG.toString(16).padStart(2, '0')}${darkenB.toString(16).padStart(2, '0')}`;
};

// Fonction pour déterminer si le texte doit être blanc ou noir selon la luminosité du fond
const getContrastTextColor = (bgColor) => {
  if (!bgColor) return 'FFFFFF';
  
  const r = parseInt(bgColor.slice(0, 2), 16);
  const g = parseInt(bgColor.slice(2, 4), 16);
  const b = parseInt(bgColor.slice(4, 6), 16);
  
  // Calcul de la luminosité (formule standard)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Si la luminosité est élevée (couleur claire), on retourne du texte noir, sinon blanc
  return luminance > 0.5 ? '000000' : 'FFFFFF';
};

const LineList = () => {
  const { networkId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [networkName, setNetworkName] = useState('');
  const [filteredLines, setFilteredLines] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedLine, setSelectedLine] = useState(null);
  const [networkLines, setNetworkLines] = useState([]);
  const [showNetworkConfirmModal, setShowNetworkConfirmModal] = useState(false);
  const [favoriteLinesCount, setFavoriteLinesCount] = useState(0);
  const [isNetworkFavorite, setIsNetworkFavorite] = useState(false);
  const [starAnimation, setStarAnimation] = useState(false);
  const [favoriteLines, setFavoriteLines] = useState([]);

  // Mettre à jour le titre de la page avec le nom du réseau
  useEffect(() => {
    if (networkName) {
      document.title = `Bus Connect - Réseau de bus ${networkName}`;
    } else {
      document.title = "Bus Connect - Réseau de bus";
    }
  }, [networkName]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch network name using cache
        const networkData = await getCachedData(
          `network-${networkId}`,
          async () => {
            const { data, error } = await supabase
              .from('networks')
              .select('network_name')
              .eq('network_id', networkId)
              .single();
            if (error) throw error;
            return data;
          }
        );
        setNetworkName(networkData.network_name);

        // Fetch all lines
        const { data: linesData, error: linesError } = await supabase
          .from('routes')
          .select('*')
          .eq('network_id', networkId);

        if (linesError) throw linesError;
        
        linesData.sort((a, b) => {
          // D'abord vérifier display_order
          if (a.display_order != null && b.display_order != null) {
            if (a.display_order < b.display_order) return -1;
            if (a.display_order > b.display_order) return 1;
          } else if (a.display_order != null) {
            return -1;
          } else if (b.display_order != null) {
            return 1;
          }

          // Ensuite vérifier route_sort_order si display_order est NULL
          if (a.route_sort_order != null && b.route_sort_order != null) {
            if (a.route_sort_order < b.route_sort_order) return -1;
            if (a.route_sort_order > b.route_sort_order) return 1;
          } else if (a.route_sort_order != null) {
            return -1;
          } else if (b.route_sort_order != null) {
            return 1;
          }

          // Enfin, utiliser route_short_name comme fallback
          return String(a.route_short_name).localeCompare(String(b.route_short_name), undefined, { numeric: true, sensitivity: 'base' });
        });

        setLines(linesData);
        setFilteredLines(linesData);
        setLoading(false);

        // Check if network is favorite
        if (user) {
          const { data: favoriteNetwork, error: favoriteNetworkError } = await supabase
            .from('favorite_networks')
            .select('*')
            .eq('user_id', user.id)
            .eq('network_id', networkId);

          if (!favoriteNetworkError && favoriteNetwork && favoriteNetwork.length > 0) {
            setIsNetworkFavorite(true);
          }

          // Récupérer les lignes favorites pour ce réseau
          const { data: favoriteLines, error: favoriteLinesError } = await supabase
            .from('favorite_lines')
            .select('*')
            .eq('user_id', user.id)
            .eq('network_id', networkId);

          if (!favoriteLinesError && favoriteLines) {
            setFavoriteLinesCount(favoriteLines.length);
            
            // Identifier les lignes favorites dans la liste complète des lignes
            const favoriteLineIds = favoriteLines.map(fav => fav.line_id);
            const userFavoriteLines = linesData.filter(line => 
              favoriteLineIds.includes(line.route_id)
            );
            setFavoriteLines(userFavoriteLines);
            
            // Marquer les lignes favorites dans la liste complète
            const updatedLines = linesData.map(line => ({
              ...line,
              is_favorite: favoriteLineIds.includes(line.route_id)
            }));
            setLines(updatedLines);
            setFilteredLines(updatedLines);
          }
        }
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [networkId, user]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      // Trier les lignes : d'abord les lignes actives (etat = 1), puis les lignes désactivées (etat = 0)
      const sortedLines = [...lines].sort((a, b) => {
        // D'abord trier par état (1 avant 0)
        if (a.etat !== b.etat) {
          return b.etat - a.etat;
        }
        // Ensuite, appliquer le tri existant pour les lignes de même état
        if (a.display_order != null && b.display_order != null) {
          if (a.display_order < b.display_order) return -1;
          if (a.display_order > b.display_order) return 1;
        } else if (a.display_order != null) {
          return -1;
        } else if (b.display_order != null) {
          return 1;
        }

        // Vérifier route_sort_order si display_order est NULL
        if (a.route_sort_order != null && b.route_sort_order != null) {
          if (a.route_sort_order < b.route_sort_order) return -1;
          if (a.route_sort_order > b.route_sort_order) return 1;
        } else if (a.route_sort_order != null) {
          return -1;
        } else if (b.route_sort_order != null) {
          return 1;
        }

        return String(a.route_short_name).localeCompare(String(b.route_short_name), undefined, { numeric: true, sensitivity: 'base' });
      });
      setFilteredLines(sortedLines);
    } else {
      const filtered = lines.filter(line => 
        line.route_short_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        line.route_long_name.toLowerCase().includes(searchTerm.toLowerCase())
      ).sort((a, b) => {
        // Maintenir le tri par état même dans les résultats de recherche
        if (a.etat !== b.etat) {
          return b.etat - a.etat;
        }
        return 0;
      });
      setFilteredLines(filtered);
    }
  }, [searchTerm, lines]);

  const handleLineClick = async (lineId, etat) => {
    // Si la ligne est désactivée, ne rien faire
    if (etat === 0) return;

    try {
      // Vérifier le nombre de directions pour cette ligne
      const { data: directionsData, error: directionsError } = await supabase.rpc(
        'get_route_directions',
        {
          route_id_param: lineId,
          network_id_param: networkId
        }
      );

      if (directionsError) throw directionsError;

      // Si la ligne a exactement une direction, rediriger directement vers les horaires
      if (directionsData && directionsData.length === 1) {
        const directionId = directionsData[0].direction_id;
        navigate(`/network/${networkId}/line/${lineId}/direction/${directionId}/timetable`);
      } else {
        // Sinon, aller vers la page des directions
        navigate(`/network/${networkId}/line/${lineId}/directions`);
      }
    } catch (err) {
      console.error("Erreur lors de la vérification des directions:", err);
      // En cas d'erreur, naviguer vers la page des directions par défaut
      navigate(`/network/${networkId}/line/${lineId}/directions`);
    }
  };

  const handleFavoriteClick = async (e, line) => {
    e.stopPropagation();
    
    // Si la ligne est désactivée, ne pas permettre l'ajout aux favoris
    if (line.etat === 0) {
      return;
    }
    
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      // Vérifier si la ligne est déjà en favoris
      const { data: lineData, error: lineError } = await supabase
        .from('favorite_lines')
        .select('*')
        .eq('user_id', user.id)
        .eq('network_id', networkId)
        .eq('line_id', line.route_id);

      if (lineError) throw lineError;
      
      if (lineData && lineData.length > 0) {
        // Si la ligne est en favoris, on la supprime
        await removeFavoriteLine(networkId, line.route_id);
        
        // Mettre à jour le compteur de lignes favorites
        setFavoriteLinesCount(prev => Math.max(0, prev - 1));
        
        // Mettre à jour la liste des lignes favorites
        setFavoriteLines(prevFavorites => 
          prevFavorites.filter(fav => fav.route_id !== line.route_id)
        );
        
        // Vérifier s'il reste des lignes favorites pour ce réseau
        const { count, error: countError } = await supabase
          .from('favorite_lines')
          .select('*', { count: 'exact' })
          .eq('user_id', user.id)
          .eq('network_id', networkId);
          
        if (!countError && count === 0) {
          // S'il n'y a plus de lignes favorites, mettre à jour le statut du réseau
          setIsNetworkFavorite(false);
        }
      } else {
        // Si la ligne n'est pas en favoris, on vérifie d'abord si le réseau est en favoris
        const { data: networkData, error: networkError } = await supabase
          .from('favorite_networks')
          .select('*')
          .eq('user_id', user.id)
          .eq('network_id', networkId);

        if (networkError) throw networkError;

        // Si le réseau n'est pas en favoris, on l'ajoute
        if (!networkData || networkData.length === 0) {
          await addFavoriteNetwork(networkId);
          setIsNetworkFavorite(true);
          
          // Animation de l'étoile
          setStarAnimation(true);
          setTimeout(() => setStarAnimation(false), 600);
        }

        // Puis on ajoute la ligne
        await addFavoriteLine({
          networkId,
          lineId: line.route_id,
          lineName: line.route_short_name,
          lineColor: line.route_color,
          lineLongName: line.route_long_name,
          lineTextColor: line.route_text_color
        });
        
        // Mettre à jour le compteur de lignes favorites
        setFavoriteLinesCount(prev => prev + 1);
        
        // Ajouter la ligne à la liste des favoris
        setFavoriteLines(prevFavorites => [...prevFavorites, line]);
        
        // Animation de l'étoile si elle n'est pas déjà animée
        if (!starAnimation) {
          setStarAnimation(true);
          setTimeout(() => setStarAnimation(false), 600);
        }
      }
      
      // Mettre à jour l'état local de la ligne
      setLines(prevLines => 
        prevLines.map(l => 
          l.route_id === line.route_id 
            ? { ...l, is_favorite: !l.is_favorite } 
            : l
        )
      );

      // Mettre à jour les lignes filtrées également
      setFilteredLines(prevFiltered => 
        prevFiltered.map(l => 
          l.route_id === line.route_id 
            ? { ...l, is_favorite: !l.is_favorite } 
            : l
        )
      );
    } catch (error) {
      console.error('Erreur lors de la modification des favoris:', error);
    }
  };

  const handleNetworkFavoriteClick = async (e) => {
    e.stopPropagation();
    
    if (!user) {
      navigate('/login');
      return;
    }

    if (isNetworkFavorite && favoriteLinesCount > 0) {
      // Show confirmation modal if there are favorite lines
      setShowNetworkConfirmModal(true);
    } else if (isNetworkFavorite) {
      // Remove from favorites directly if no favorite lines
      await handleRemoveNetworkFavorite();
    } else {
      // Add to favorites
      try {
        await addFavoriteNetwork(networkId);
        setIsNetworkFavorite(true);
      } catch (error) {
        console.error('Erreur lors de l\'ajout aux favoris:', error);
      }
    }
  };

  const handleRemoveNetworkFavorite = async () => {
    try {
      // Supprimer le réseau des favoris
      await removeFavoriteNetwork(networkId);
      
      // Supprimer toutes les lignes favorites de ce réseau
      if (favoriteLinesCount > 0) {
        const { error } = await supabase
          .from('favorite_lines')
          .delete()
          .match({ user_id: user.id, network_id: networkId });
          
        if (error) throw error;
      }
      
      // Mettre à jour l'état local
      setIsNetworkFavorite(false);
      setFavoriteLinesCount(0);
      setShowNetworkConfirmModal(false);
      
      // Mettre à jour l'état des lignes pour refléter qu'elles ne sont plus en favoris
      setLines(lines => lines.map(line => ({
        ...line,
        is_favorite: false
      })));
      
    } catch (error) {
      console.error('Erreur lors de la suppression des favoris:', error);
    }
  };

  // Filtrer les favoris en fonction de la recherche
  const filteredFavoriteLines = searchTerm.trim() === '' 
    ? favoriteLines 
    : favoriteLines.filter(line => 
        line.route_short_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        line.route_long_name.toLowerCase().includes(searchTerm.toLowerCase())
      );

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="flex flex-col items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p className="mt-4 text-blue-600 font-medium">Chargement des lignes...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="bg-red-50 p-6 rounded-lg shadow-sm border border-red-200 max-w-lg">
        <div className="flex items-center">
          <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h3 className="ml-2 text-lg font-semibold text-red-800">Erreur</h3>
        </div>
        <p className="mt-2 text-red-700">{error}</p>
        <Link to="/" className="mt-4 inline-flex items-center text-red-600 hover:text-red-800">
          <svg className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Retour à l'accueil
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900 transition-colors duration-200">
      <AlertBanner networkId={networkId} />
      <style>{additionalStyles}</style>
      {/* Header */}
      <div className="bg-white dark:bg-dark-800 shadow-sm relative transition-colors duration-200">
        <div className="absolute top-0 left-0 z-10">
          <Link to="/" className="flex items-center justify-center w-16 h-16 bg-gray-50 dark:bg-dark-700 rounded-br-full relative hover:bg-gray-100 dark:hover:bg-dark-600 transition-colors">
            <div className="absolute transform -translate-x-2 -translate-y-2">
              <svg className="h-7 w-7 text-gray-700 dark:text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </div>
          </Link>
        </div>
        <div className="absolute top-0 right-0 z-10">
          <div className="p-4">
            <button 
              onClick={handleNetworkFavoriteClick}
              className="focus:outline-none"
            >
              <svg 
                className={`h-10 w-10 ${isNetworkFavorite ? '' : 'text-gray-400 dark:text-gray-500'}`} 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
                fill={isNetworkFavorite ? FAVORITE_COLOR : "none"}
                strokeWidth={isNetworkFavorite ? 0 : 1.5}
                color={isNetworkFavorite ? FAVORITE_COLOR : "currentColor"}
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" 
                />
              </svg>
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center">
            <div className="w-full h-24 flex items-center justify-center">
              <div 
                className="flex items-center justify-center"
                style={{
                  transform: 'scale(1.8)'
                }}
              >
                <div className="max-w-[70px] max-h-[50px] flex items-center justify-center">
                  <NetworkLogo 
                    networkId={networkId}
                    networkName={networkName}
                    size="large"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: 'auto',
                      height: 'auto',
                      display: 'block',
                      margin: '0 auto'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de confirmation pour retirer un réseau des favoris */}
      {showNetworkConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-800 rounded-xl max-w-md w-full mx-4 overflow-hidden shadow-2xl transform transition-all">
            <div className="bg-gradient-to-r from-red-500 to-red-600 py-4 px-5">
              <div className="flex items-center">
                <svg className="h-7 w-7 text-white mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <h3 className="text-xl font-semibold text-white">Supprimer des favoris</h3>
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-start mb-4">
                <div className="flex-shrink-0 bg-red-50 dark:bg-red-900 rounded-full p-2 mr-3">
                  <svg className="h-6 w-6 text-red-500 dark:text-red-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="text-gray-800 dark:text-white font-medium text-lg mb-1">
                    Attention
                  </p>
                  <p className="text-gray-600 dark:text-gray-300">
                    Ce réseau a <span className="font-semibold text-red-600 dark:text-red-400">{favoriteLinesCount}</span> ligne{favoriteLinesCount > 1 ? 's' : ''} en favoris.
                    <br />
                    Souhaitez-vous quand même le retirer de vos favoris ?
                  </p>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  className="px-5 py-2.5 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
                  onClick={() => setShowNetworkConfirmModal(false)}
                >
                  Annuler
                </button>
                <button
                  className="px-5 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors flex items-center"
                  onClick={handleRemoveNetworkFavorite}
                >
                  <svg className="h-4 w-4 mr-1.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Retirer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search bar avec un style amélioré */}
      <div className="bg-white dark:bg-dark-800 border-b border-gray-100 dark:border-dark-700 transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3">
          <div className="search-container relative">
            <div className="search-icon">
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher une ligne..."
              className="search-input dark:bg-dark-700 dark:text-white dark:placeholder-gray-400"
            />
            {searchTerm && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <button 
                  onClick={() => setSearchTerm('')}
                  className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400 transition-colors"
                >
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <main className="max-w-7xl mx-auto py-3 sm:py-6 px-3 sm:px-6 lg:px-8 mb-16 sm:mb-0">
        {filteredLines.length === 0 ? (
          <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm p-6 text-center transition-colors duration-200">
            <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Aucune ligne trouvée</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Essayez avec un autre terme de recherche.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Section des lignes favorites */}
            {user && filteredFavoriteLines.length > 0 && (
              <div>
                <h2 className="page-title text-gradient">Vos lignes favorites</h2>
                <hr className="gradient-hr" />
                <div className="grid grid-cols-1 gap-3">
                  {filteredFavoriteLines.map((line) => {
                    const backgroundColor = darkenColor(line.route_color);
                    const textColor = getContrastTextColor(backgroundColor);
                    const isDisabled = line.etat === 0;
                    
                    return (
                      <div
                        key={`fav-${line.route_id}`}
                        onClick={() => handleLineClick(line.route_id, line.etat)}
                        className={`block ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <div className={`bg-white dark:bg-dark-800 rounded-lg shadow-sm transition-all duration-200 overflow-hidden border border-gray-100 dark:border-dark-700 ${isDisabled ? 'opacity-50' : 'hover:shadow-md hover:border-blue-400 dark:hover:border-blue-500'}`}>
                          <div className="flex items-center p-2 sm:p-3">
                            <div className="flex-shrink-0">
                              <div 
                                className="w-20 h-10 sm:w-24 sm:h-10 rounded-[8px] flex items-center justify-center text-white font-bold text-sm sm:text-base px-1"
                                style={{ 
                                  backgroundColor: `#${backgroundColor}`,
                                  color: `#${textColor}`
                                }}
                              >
                                <span className="truncate max-w-full font-naotypo-bold">{line.route_short_name}</span>
                              </div>
                            </div>
                            <div className="ml-3 sm:ml-4 flex-grow min-w-0 mr-2">
                              <div className="flex flex-col">
                                <p className="text-xs text-gray-500 dark:text-gray-400">Ligne</p>
                                <TextMarquee text={line.route_long_name || line.route_short_name} />
                                {isDisabled && (
                                  <p className="text-xs text-red-500 mt-1">Ligne temporairement désactivée</p>
                                )}
                              </div>
                            </div>
                            <div className="flex-shrink-0 flex items-center space-x-1 sm:space-x-2">
                              <FavoriteButton
                                type="line"
                                item={{
                                  networkId: networkId,
                                  lineId: line.route_id,
                                  lineName: line.route_short_name,
                                  lineColor: line.route_color,
                                  lineLongName: line.route_long_name,
                                  lineTextColor: textColor
                                }}
                                onClick={(e) => handleFavoriteClick(e, line)}
                                disabled={line.etat === 0}
                                className={line.etat === 0 ? 'opacity-50 cursor-not-allowed' : ''}
                              />
                              <div className="flex-shrink-0 text-gray-400 dark:text-gray-500">
                                <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Liste principale des lignes - excluant celles des favoris */}
            <div>
              {user && filteredFavoriteLines.length > 0 && (
                <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
                  Toutes les lignes
                </h2>
              )}
              <div className="grid grid-cols-1 gap-3">
                {filteredLines
                  .filter(line => !favoriteLines.some(fav => fav.route_id === line.route_id) || searchTerm.trim() !== '')
                  .map((line) => {
                    const backgroundColor = darkenColor(line.route_color);
                    const textColor = getContrastTextColor(backgroundColor);
                    const isDisabled = line.etat === 0;
                    
                    return (
                      <div
                        key={line.route_id}
                        onClick={() => handleLineClick(line.route_id, line.etat)}
                        className={`block ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <div className={`bg-white dark:bg-dark-800 rounded-lg shadow-sm transition-all duration-200 overflow-hidden border border-gray-100 dark:border-dark-700 ${isDisabled ? 'opacity-50' : 'hover:shadow-md hover:border-blue-400 dark:hover:border-blue-500'}`}>
                          <div className="flex items-center p-2 sm:p-3">
                            <div className="flex-shrink-0">
                              <div 
                                className="w-20 h-10 sm:w-24 sm:h-10 rounded-[8px] flex items-center justify-center text-white font-bold text-sm sm:text-base px-1"
                                style={{ 
                                  backgroundColor: `#${backgroundColor}`,
                                  color: `#${textColor}`
                                }}
                              >
                                <span className="truncate max-w-full font-naotypo-bold">{line.route_short_name}</span>
                              </div>
                            </div>
                            <div className="ml-3 sm:ml-4 flex-grow min-w-0 mr-2">
                              <div className="flex flex-col">
                                <p className="text-xs text-gray-500 dark:text-gray-400">Ligne</p>
                                <TextMarquee text={line.route_long_name || line.route_short_name} />
                                {isDisabled && (
                                  <p className="text-xs text-red-500 mt-1">Ligne temporairement désactivée</p>
                                )}
                              </div>
                            </div>
                            <div className="flex-shrink-0 flex items-center space-x-1 sm:space-x-2">
                              <FavoriteButton
                                type="line"
                                item={{
                                  networkId: networkId,
                                  lineId: line.route_id,
                                  lineName: line.route_short_name,
                                  lineColor: line.route_color,
                                  lineLongName: line.route_long_name,
                                  lineTextColor: textColor
                                }}
                                onClick={(e) => handleFavoriteClick(e, line)}
                                disabled={line.etat === 0}
                                className={line.etat === 0 ? 'opacity-50 cursor-not-allowed' : ''}
                              />
                              <div className="flex-shrink-0 text-gray-400 dark:text-gray-500">
                                <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default LineList; 